import React from 'react';
import './styles/orderprogress-vertical.css';

interface OrderProgressVerticalProps {
  trackingHistory: Array<{ status: string; timestamp: string; location?: string; notes?: string }>;
  orderStatus?: string;
  createdAt?: string;
}

const OrderProgressVertical: React.FC<OrderProgressVerticalProps> = ({ 
  trackingHistory, 
  orderStatus,
  createdAt 
}) => {
  // Define the order status flow
  const statusFlow = [
    { key: 'pending', label: 'Order Placed' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'preparing', label: 'Packed at Warehouse' },
    { key: 'out_for_delivery', label: 'Out for Delivery' },
    { key: 'delivered', label: 'Delivered' }
  ];

  // Get current step index based on order status
  const getCurrentStepIndex = () => {
    const index = statusFlow.findIndex(step => step.key === orderStatus);
    return index >= 0 ? index : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  // Format timestamp to show time only
  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Calculate delivery time in minutes
  const getDeliveryTimeText = () => {
    if (!createdAt || orderStatus !== 'delivered') return '';
    
    const deliveredEntry = trackingHistory.find(h => h.status === 'delivered');
    if (!deliveredEntry || !deliveredEntry.timestamp) return '';
    
    const createdDate = new Date(createdAt);
    const deliveredDate = new Date(deliveredEntry.timestamp);
    const diffMs = deliveredDate.getTime() - createdDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `Delivered in ${diffMinutes} Min`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `Delivered in ${hours}h ${minutes}m`;
    }
  };

  // Get timestamp for each status from tracking history
  const getStatusTime = (statusKey: string, index: number) => {
    // For delivered status when it's the active step, show delivery time
    if (index === currentStepIndex && orderStatus === 'delivered') {
      return getDeliveryTimeText();
    }
    
    // If this is the active step (not delivered), don't show time
    if (index === currentStepIndex) {
      return '';
    }
    
    // For completed steps only
    if (index < currentStepIndex) {
      // For first step (Order Placed/pending), always use createdAt
      if (index === 0 && createdAt) {
        return formatTime(createdAt);
      }
      
      // Try to find exact match in tracking history
      const entry = trackingHistory.find(h => h.status === statusKey);
      if (entry && entry.timestamp) {
        return formatTime(entry.timestamp);
      }
      
      // For intermediate completed steps without exact tracking entry,
      // find the earliest entry that comes after this step in the flow
      const laterEntries = trackingHistory.filter(h => {
        const entryIndex = statusFlow.findIndex(s => s.key === h.status);
        return entryIndex > index; // Find entries that come after this step
      });
      
      if (laterEntries.length > 0) {
        // Use the earliest timestamp from steps that came after
        const sortedEntries = laterEntries.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        return formatTime(sortedEntries[0].timestamp);
      }
      
      // If no later entries found, use createdAt as fallback
      if (createdAt) {
        return formatTime(createdAt);
      }
    }
    
    return '';
  };

  // Determine step status: completed, active, or pending
  const getStepStatus = (index: number) => {
    if (orderStatus === 'cancelled') return 'cancelled';
    if (index < currentStepIndex) return 'completed';
    if (index === currentStepIndex) return 'active';
    return 'pending';
  };

  // Filter steps to show only completed and active ones
  const visibleSteps = statusFlow.filter((_step, index) => index <= currentStepIndex);

  return (
    <div className="order-progress-vertical">
      {/* BuildZap Logo */}
      <div className="progress-header">
        <img 
          src="/images/logoname.png" 
          alt="BuildZap" 
          className="buildzap-logo"
        />
      </div>

      {/* Vertical Progress Steps - always visible */}
      <div className="progress-steps">
        {visibleSteps.map((step, visibleIndex) => {
          const originalIndex = statusFlow.findIndex(s => s.key === step.key);
          const stepStatus = getStepStatus(originalIndex);
          const timeStamp = getStatusTime(step.key, originalIndex);
          
          return (
            <div key={step.key} className="progress-step-container">
              <div className={`progress-step ${stepStatus}`}>
                {/* Status Icon */}
                <div className={`step-icon ${stepStatus}`}>
                  {stepStatus === 'completed' && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path 
                        d="M16.6668 5L7.50016 14.1667L3.3335 10" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {stepStatus === 'active' && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path 
                        d="M16.6668 5L7.50016 14.1667L3.3335 10" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>

                {/* Status Text */}
                <div className="step-content">
                  <p className="step-label">{step.label}</p>
                  <p className="step-time">{timeStamp || 'In Progress'}</p>
                </div>
              </div>

              {/* Connector Line */}
              {visibleIndex < visibleSteps.length - 1 && (
                <div className={`step-connector ${stepStatus}`}>
                  <div className="connector-line"></div>
                  <div className="connector-dot"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderProgressVertical;
